import React, { useEffect, useState } from 'react'
import UrlPattern from 'url-pattern'

/**
 * A wrapper over an untyped `React.Route` that adds a type `R` to the captured parameters of the route
 */
export interface Route<R> {
  /**
   * The title to show in the browser's tab, when the route matches.
   *
   * If not given defaults to empty string.
   */
  title?: string

  /** The underlying URL path-template of the untyped `React.Route`.
   *
   * - It may be a hardcoded (relative)url path.
   * - It may include the HTTP query parameters.
   * - It can capture parameters.
   *
   * Note: all templates use `exact` matching and no partial matching.
   *
   * Template URL examples:
   * - `/sometext`  , Exact matching of the string
   * - `/static/:foo/more` , Captures to the `foo` variable a url segment as string. This string will never contain forward slashes (i.e. single url segment). String can be empty.
   * - `/:foo/:bar?/static` , An optional url segment, the leading slash in front of `/:bar?` is optional in case `:bar` is empty.
   * - `/:segs*` , 0 or more url segments captured in a single `segs` string-variable. The string may contain slashes, thus can be turned into an array of segments with `segs.splt('/')`
   * - `/:segs(\\d+-[^/]*)+` , A variable followed by parentheses is a named regex. In this case, it matches 1 or more segments as in the example url: `1-somedecr1ption/6491237-othEr_Description/...`
   *
   * For more info on route templates, see the official repository: <https://github.com/pillarjs/path-to-regexp>
   */
  path?: string

  /**
   * A function that gets as input the untyped captured parameters returned the React-Router parser,
   * and returns an equivalent but typed representation of the parameters, typed by `R`.
   */
  paramsToRoute: (_: any) => R
}

/**
 * A helper function to create a typed-[[Route]] with no title.
 */
export const route = <P, R>(path: string, paramsToRoute: (_: P) => R): Route<R> => ({ path, paramsToRoute })

/**
 * A helper function to construct a typed [[Route]] which has no path and no parameters.
 * It acts as a **catch-all** (default) router.
 */
export const notFoundRouteCase = <R,>(makeRoute: () => R): Route<R> => ({ paramsToRoute: makeRoute })

type Location = { pathname: string; search: string }
const getCurrentLocation = (): Location => {
  const { pathname, search } = window.location
  return { pathname: pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname, search: search }
}
const getMatchedRoute = <o,>(location: Location, routes: Array<Route<o>>) =>
  routes
    .map((route) => ({
      route: route,
      match: route.path === undefined ? null : new UrlPattern(route.path).match(location.pathname),
    }))
    .filter((routeAndMatch) => routeAndMatch.match !== null)[0]
const getParams = (location: Location, match: any) => {
  const searchParams: { [key: string]: undefined | string | string[] } = {}
  for (const [key, value] of new URLSearchParams(location.search)) {
    const currentValue = searchParams[key]
    if (searchParams[key] === undefined) searchParams[key] = value
    if (typeof currentValue === 'string') searchParams[key] = [currentValue, value]
    if (Array.isArray(currentValue)) searchParams[key] = [...currentValue, value]
  }
  const params = { ...match, ...searchParams }
  return params
}

export const initialRoute = <o,>(routes: Array<Route<o>>) => {
  const currentLocation = getCurrentLocation()
  const matchedRoute = getMatchedRoute(currentLocation, routes)
  if (matchedRoute !== undefined) {
    const params = getParams(currentLocation, matchedRoute.match)
    return matchedRoute.route.paramsToRoute(params)
  }
}

export const Router = <o,>(props: { routes: Array<Route<o>>; setState: (_: o) => void }) => {
  const [location, setLocation] = useState<Location>(getCurrentLocation())
  useEffect((): (() => void) => {
    const checkRouteChanges = setInterval(() => {
      const currentLocation = getCurrentLocation()
      if (currentLocation.pathname !== location.pathname || currentLocation.search !== location.search) {
        setLocation(currentLocation)
        const matchedRoute = getMatchedRoute(currentLocation, props.routes)
        if (matchedRoute !== undefined) {
          const params = getParams(currentLocation, matchedRoute.match)
          props.setState(matchedRoute.route.paramsToRoute(params))
          if (matchedRoute.route.title !== undefined) document.title = matchedRoute.route.title
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: "instant",
            });
          }, 10);
        }
      }
    }, 100)
    return () => clearInterval(checkRouteChanges)
  }, [location, props.routes])

  return <></>
}
